import {GameModel} from "@nolimitcity/slot-game/bin/model/GameModel";
import {
    BIG_WIN_LEVEL_NAMES,
    IBetWayWinData,
    IBigWinLevel,
    IFeatureInfo,
    IKMDGameData,
    IKMDInitData,
    INormalWinConfig,
    IS1FeatureInfo,
    NORMAL_WIN_LEVEL_NAMES,
    SPECIAL_SYMBOL_NAMES
} from "./IKMDData";
import {ApiPlugin} from "@nolimitcity/slot-launcher/bin/plugins/ApiPlugin";
import {KennethMustDie} from "../KennethMustDie";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {KMDGameMode} from "./KMDGameMode";
import {SimpleReelModelSymbolData} from "../../core/reel/simplereel/SimpleReelModel";
import {APIEvent} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {APIFeatureBetEventData} from "@nolimitcity/slot-launcher/bin/interfaces/APIBetEventData";

export class KMDModel extends GameModel<IKMDInitData, IKMDGameData> {

    public isRestore: boolean = false;
    public xWaysMainReels: IFeatureInfo[] = [];
    public xWaysSpecialRow: IFeatureInfo[] = [];
    public isNormalWin: boolean = false;
    public isBigWin: boolean = false;
    public winRatio: number;
    public xNudgeMainReels: IFeatureInfo[] = [];
    public xNudgeSpecialRow: IFeatureInfo[] = [];
    public xHPos: number = -1;
    public s1Positions: number[];
    public s2Feature: IFeatureInfo;
    public reSpawnPositions: boolean[][] = [];
    public nearWinReels: boolean[] = [];
    public specialBetWayWins: IBetWayWinData[] = [];
    public bigWinLevelRatioStarter: number = 15;
    public multipliersForReelStop: number[][];
    public isSFSTriggered: boolean;

    private bigWinLevelRatioMultiplier: number = 2;

    constructor(api: ApiPlugin, triggerEvents: boolean = true) {
        super(api, triggerEvents);

        api.events.on(APIEvent.BET, (data: APIFeatureBetEventData) => this.onBet(data));
        api.events.on(APIEvent.ACTION_SPINS_BET, (data: APIFeatureBetEventData) => this.onBet(data)); // #345
        api.events.once("featureName", (name: string) => {
            KennethMustDie.buyFeatureName = name;
        });
    }

    public getNormalWinConfig(): INormalWinConfig {
        const ratio: number = this.winRatio;

        if (ratio >= 5) {
            return {
                duration: 0.5,
                name: NORMAL_WIN_LEVEL_NAMES.LARGE
            };
        } else if (ratio >= 3) {
            return {
                duration: 0.001,
                name: NORMAL_WIN_LEVEL_NAMES.MEDIUM
            };
        } else if (ratio >= 1.5) {
            return {
                duration: 0.001,
                name: NORMAL_WIN_LEVEL_NAMES.SMALL
            };
        } else {
            return {
                duration: 0.001,
                name: NORMAL_WIN_LEVEL_NAMES.TINY
            };
        }
    }

    public getBigWinLevels(): IBigWinLevel[] {
        let levels: IBigWinLevel[] = [];
        const ratio: number = this.winRatio,
            multiplier: number = this.bigWinLevelRatioMultiplier,
            isLevel1Pushed: boolean = (!this.gameData.wasFeatureBuy);

        if (isLevel1Pushed) {
            if (ratio >= (15 * multiplier)) {
                levels.push({
                    from: 0,
                    to: 25 * multiplier,
                    duration: 4,
                    id: 1,
                    name: BIG_WIN_LEVEL_NAMES.BIG_WIN_01
                });
            }
        }

        if (ratio >= (25 * multiplier)) {
            levels.push({
                from: (isLevel1Pushed ? (25 * multiplier) : 0),
                to: 50 * multiplier,
                duration: 4,
                id: 2,
                name: BIG_WIN_LEVEL_NAMES.BIG_WIN_02
            });
        }

        if (ratio >= (50 * multiplier)) {
            levels.push({
                from: 50 * multiplier,
                to: 100 * multiplier,
                duration: 5,
                id: 3,
                name: BIG_WIN_LEVEL_NAMES.BIG_WIN_03
            });
        }

        if (ratio >= (100 * multiplier)) {
            levels.push({
                from: 100 * multiplier,
                to: 1000 * multiplier,
                duration: 6,
                id: 4,
                name: BIG_WIN_LEVEL_NAMES.BIG_WIN_04
            });
        }

        if (ratio >= (1000 * multiplier)) {
            levels.push({
                from: 1000 * multiplier,
                to: 10000 * multiplier,
                duration: 6,
                id: 5,
                name: BIG_WIN_LEVEL_NAMES.BIG_WIN_05
            });
        }

        if (ratio >= (10000 * multiplier)) {
            levels.push({
                from: 10000 * multiplier,
                to: 10000 * multiplier,
                duration: 0.001,
                id: 6,
                name: BIG_WIN_LEVEL_NAMES.BIG_WIN_06
            });
        }

        levels = this.setLastConfig(ratio, levels);

        return levels;
    }

    public setIsSFSTriggered(): void {
        if (!this.isSFSTriggered) {
            this.isSFSTriggered = (this.xHPos === 1 && this.s2Feature.originReelId === (KennethMustDie.REELS_NUM - 1) && KennethMustDie.s1CountReelStop === 3);
        }
    }

    public onGame(data: IKMDGameData): void {
        super.onGame(data);
        this.isRestore = false;

        // making the reels 5*3 grid, server sends out as 5*4.
        // #538
        if (data.reels[0].length === 4) {
            data.reels.forEach((reel: string[]) => {
                reel.pop();
            });
        }

        // sorting the array so that the special reel is ignited first in case of big wins, otherwise it doesn't matter
        data.betWayWins.sort((a: IBetWayWinData, b: IBetWayWinData) => {
            return (+(b.reelWinPositions[1][3] || b.reelWinPositions[2][3] || b.reelWinPositions[3][3]) -
                +(a.reelWinPositions[1][3] || a.reelWinPositions[2][3] || a.reelWinPositions[3][3]));
        });
        // sorting the array so that the special reel is ignited first in case of big wins, otherwise it doesn't matter
        data.specialBetWayWins && data.specialBetWayWins.sort((a: IBetWayWinData, b: IBetWayWinData) => {
            return (+(b.reelWinPositions[1][3] || b.reelWinPositions[2][3] || b.reelWinPositions[3][3]) -
                +(a.reelWinPositions[1][3] || a.reelWinPositions[2][3] || a.reelWinPositions[3][3]));
        });

        const reels: string[][] = data.reels,
            mode: string = this.data.mode;

        this.xHPos = (reels[2].indexOf(SPECIAL_SYMBOL_NAMES.XH));
        this.winRatio = (data.totalAvalancheRoundWinnings / data.playedBetValue);

        // #332
        let multiplier: number = 2;
        if (mode === KMDGameMode.FREESPIN || mode === KMDGameMode.FREESPIN_AVALANCHE) {
            multiplier = 3;
        } else if (mode === KMDGameMode.FREESPIN_COOL || mode === KMDGameMode.FREESPIN_COOL_AVALANCHE) {
            multiplier = 4;
        } else if (mode === KMDGameMode.FREESPIN_SUPER || mode === KMDGameMode.FREESPIN_SUPER_AVALANCHE) {
            multiplier = 5;
        }
        this.bigWinLevelRatioMultiplier = multiplier;
        this.bigWinLevelRatioStarter = ((!this.gameData.wasFeatureBuy ? 15 : 25) * multiplier);

        this.setWinCategory(data.nextMode, data.totalAvalancheRoundWinnings);
        this.s1Positions = this.calculateS1Positions(reels);

        const s1FeatureInfo: IS1FeatureInfo[] = this.calculateS1Feature(data.s1TransformList);

        this.s2Feature = this.calculateS2Feature(reels[KennethMustDie.REELS_NUM - 1]);
        this.calculateXWaysData(data.specialReels, s1FeatureInfo);
        this.calculateXNudgeData(data.specialReels, reels, s1FeatureInfo);

        // #326, reset on, any non-avalanche game mode
        if (data.mode.indexOf("AVALANCHE") === -1) {
            this.multipliersForReelStop = [
                [0, 0, 0, 0],
                [0, 0, 0, 0],
                [0, 0, 0, 0],
                [0, 0, 0, 0],
                [0, 0, 0, 0]
            ];
        }
        this.multipliersForReelStop[1][3] = data.multiplier[1][3];
        this.multipliersForReelStop[2][3] = data.multiplier[2][3];
        this.multipliersForReelStop[3][3] = data.multiplier[3][3];

        this.handleReSpawnPositions(data.reels);

        data.reels[0][0] = ArrayHelper.getRandomValue(KennethMustDie.LOW_SYMBOL_NAMES);
        data.reels[4][0] = ArrayHelper.getRandomValue(KennethMustDie.LOW_SYMBOL_NAMES);

        // making the spin symbol to be a low symbol as XH is already sticky there
        if (this.data.mode === KMDGameMode.FREESPIN_SUPER) {
            reels[2][1] = ArrayHelper.getRandomValue(KennethMustDie.LOW_SYMBOL_NAMES);
        }

        this.nearWinReels = this.parseNearWinReels(reels);

        this.specialBetWayWins = data.specialBetWayWins || [];

        KennethMustDie.reelAreaSymbolsName = [
            ["", "", ""],
            ["", "", ""],
            ["", "", ""],
            ["", "", ""],
            ["", "", ""]
        ];

        KennethMustDie.mode = data.mode;
    }

    protected onInit(data: IKMDInitData): void {
        super.onInit(data);

        // making the reels 5*3 grid, server sends out as 5*4.
        if (data.reels[0].length === 4) {
            data.reels.forEach((reel: string[]) => {
                reel.pop();
            });
        }

        this.isRestore = data.isRestoreState;

        // #192
        this.s2Feature = this.calculateS2Feature(data.reels[KennethMustDie.REELS_NUM - 1]);
        if (!this.isRestore) {
            data.reels[0][0] = ArrayHelper.getRandomValue(KennethMustDie.LOW_SYMBOL_NAMES);
            data.reels[4][0] = ArrayHelper.getRandomValue(KennethMustDie.LOW_SYMBOL_NAMES);

            // default multipliers in special row on game init
            data.multiplier[1][3] = 1;
            data.multiplier[2][3] = 2;
            data.multiplier[3][3] = 3;
        } else {
            // #326
            this.multipliersForReelStop = JSON.parse(JSON.stringify(data.multiplier));
        }

        KennethMustDie.reelAreaSymbolsName = [
            ["", "", ""],
            ["", "", ""],
            ["", "", ""],
            ["", "", ""],
            ["", "", ""]
        ];

        this.s1Positions = this.calculateS1Positions(data.reels);
        this.handleReSpawnPositions(data.reels);

        this.xHPos = (data.reels[2].indexOf(SPECIAL_SYMBOL_NAMES.XH));

        this.isSFSTriggered = (data.lockedFeature === 2 && data.mode.indexOf("NORMAL") > -1);

        KennethMustDie.s1CountReelStop = (this.s1Positions.filter((val: number) => {
            return (val > -1);
        }).length);

        KennethMustDie.mode = data.mode;
    }

    // #354
    private handleReSpawnPositions(reels: string[][]): void {
        this.reSpawnPositions = [
            [false, false, false, false],
            [false, false, false, false],
            [false, false, false, false],
            [false, false, false, false],
            [false, false, false, false]
        ];

        if (this.s1Positions.filter((val: number) => {
            return (val > -1);
        }).length > 1) {
            // S exists only on middle 3 reels
            for (let reelId: number = 1; reelId < this.s1Positions.length - 1; reelId++) {
                if (this.s1Positions[reelId] > -1) {
                    this.reSpawnPositions[reelId][this.s1Positions[reelId]] = true;
                }
            }
        }
        if (this.s2Feature.originReelId !== -1) {
            this.reSpawnPositions[this.s2Feature.originReelId][this.s2Feature.originSymId] = true;
        }
        this.calculateReSpawnPositionsForStickyXNudge(reels);
        this.calculateReSpawnPositionsForStickyWilds(reels);
    }

    private onBet(data: APIFeatureBetEventData): void {
        for (let index: number = 0; index < KennethMustDie.model.initData.featureBuyTimesBetValue.length; index++) {
            if (KennethMustDie.model.initData.featureBuyTimesBetValue[index].name === data.featureName) {
                KennethMustDie.buyFeatureName = data.featureName;

                return;
            }
        }
    }

    private setWinCategory(nextMode: string, winAmount: number): void {
        this.isNormalWin = this.isBigWin = false;

        if (!winAmount) {
            return;
        }

        if (nextMode.indexOf("AVALANCHE") === -1) {
            if (this.winRatio < this.bigWinLevelRatioStarter) {
                this.isNormalWin = true;
            } else {
                this.isBigWin = true;
            }
        }
    }

    private setLastConfig(winRatio: number, configs: IBigWinLevel[]): IBigWinLevel[] {
        const lastConfig: IBigWinLevel = configs[configs.length - 1];

        if (lastConfig.from * 1.2 > winRatio && configs.length > 1) {
            configs.pop();
            return this.setLastConfig(winRatio, configs);
        }
        if (lastConfig.id < 6) {
            lastConfig.to = winRatio;
        }

        return configs;
    }

    private calculateXWaysData(specialReels: string[], s1Features: IS1FeatureInfo[]): void {
        this.xWaysMainReels = [];
        s1Features.forEach((val: IS1FeatureInfo) => {
            if (val.featureName === SPECIAL_SYMBOL_NAMES.X_WAYS) {
                let item: IFeatureInfo = {
                    originReelId: val.originReelId,
                    originSymId: val.originSymId
                };

                this.xWaysMainReels.push(item);
            }
        });

        this.xWaysSpecialRow = [];
        specialReels.forEach((val: string, idx: number) => {
            if (val === SPECIAL_SYMBOL_NAMES.X_WAYS) {
                let item: IFeatureInfo = {
                    originReelId: (idx + 1),
                    originSymId: idx
                };

                this.xWaysSpecialRow.push(item);
            }
        });
    }

    private calculateXNudgeData(specialReels: string[], reels: string[][], s1Features: IS1FeatureInfo[]): void {
        this.xNudgeMainReels = [];
        reels.forEach((reel: string[], reelId: number) => {
            reel.forEach((val: string, symId: number) => {
                if (val === SPECIAL_SYMBOL_NAMES.X_NUDGE_WILD) {
                    let item: IFeatureInfo = {
                        originReelId: reelId,
                        originSymId: symId
                    };

                    this.xNudgeMainReels.push(item);
                }
            });
        });
        s1Features.forEach((val: IS1FeatureInfo) => {
            if (val.featureName === SPECIAL_SYMBOL_NAMES.X_NUDGE_WILD) {
                let item: IFeatureInfo = {
                    originReelId: val.originReelId,
                    originSymId: val.originSymId
                };

                this.xNudgeMainReels.push(item);
            }
        });

        this.xNudgeSpecialRow = [];
        specialReels.forEach((val: string, idx: number) => {
            if (val === SPECIAL_SYMBOL_NAMES.X_NUDGE_WILD) {
                let item: IFeatureInfo = {
                    originReelId: (idx + 1),
                    originSymId: idx
                };

                this.xNudgeSpecialRow.push(item);
            }
        });
    }

    private calculateS1Positions(reels: string[][]): number[] {
        const retVal: number[] = [-1, -1, -1, -1, -1];

        // S exists only on middle 3 reels
        for (let reelId: number = 1; reelId < retVal.length - 1; reelId++) {
            const s1Idx: number = reels[reelId].indexOf(SPECIAL_SYMBOL_NAMES.S);

            if (s1Idx > -1) {
                retVal[reelId] = s1Idx;
            }
        }

        return retVal;
    }

    private calculateS1Feature(s1Features: string[]): IS1FeatureInfo[] {
        let retVal: IS1FeatureInfo[] = [];

        if (s1Features.length) {
            let ctr: number = 0;

            this.s1Positions.forEach((val: number, reelId: number) => {
                if (val > -1) {
                    let featureInfo: IS1FeatureInfo = {
                        originReelId: reelId,
                        originSymId: val,
                        featureName: s1Features[ctr++]
                    };

                    retVal.push(featureInfo);
                }
            });
        }

        return retVal;
    }

    private calculateS2Feature(lastReel: string[]): IFeatureInfo {
        const retVal: IFeatureInfo = {
            originReelId: -1,
            originSymId: -1
        };

        for (let symId: number = 1; symId < lastReel.length; symId++) {
            if (lastReel[symId] === SPECIAL_SYMBOL_NAMES.S2) {
                retVal.originReelId = (KennethMustDie.REELS_NUM - 1);
                retVal.originSymId = symId;

                break;
            }
        }

        // making the spin symbol to be a low symbol as S2 is already sticky there
        if (retVal.originReelId !== -1 && (this.data.mode === KMDGameMode.FREESPIN_COOL || this.data.mode === KMDGameMode.FREESPIN_SUPER)) {
            lastReel[retVal.originSymId] = ArrayHelper.getRandomValue(KennethMustDie.LOW_SYMBOL_NAMES);
        }

        // correct position for S2 restore, as in init we have only 3 symbols per reel.
        if (this.isRestore) {
            retVal.originSymId += 1;
        }

        return retVal;
    }

    private parseNearWinReels(reels: string[][]): boolean[] {
        const retVal: boolean[] = [false, false, false, false, false];

        if (this.gameData.wasFeatureBuy) {
            return retVal;
        }

        if (this.gameData.mode === KMDGameMode.NORMAL) {
            let scatterCount: number = 0;
            // S doesn't exist on reel 0 and 4.
            for (let reel: number = 1; reel < reels.length - 1; reel++) {
                if (reels[reel].indexOf(SPECIAL_SYMBOL_NAMES.S) > -1) {
                    scatterCount++;
                }

                if (scatterCount === 2) {
                    for (let nextReel: number = (reel + 1); nextReel < reels.length - 1; nextReel++) {
                        retVal[nextReel] = true;
                    }

                    // if 3 Scatters are there, anticipate for S2, which is only possible on last reel.
                    if (reel === 2 && reels[3].indexOf(SPECIAL_SYMBOL_NAMES.S) > -1) {
                        retVal[4] = true;
                    }

                    break;
                }
            }
        } else if (this.gameData.mode === KMDGameMode.NORMAL_AVALANCHE) {
            this.parseNearWinReelsForNormalAvalanche(reels, retVal);
        }

        return retVal;
    }

    private parseNearWinReelsForNormalAvalanche(reels: string[][], retVal: boolean[]): void {
        let scatterCount: number = 0,
            prevScatterReels: number[] = [];

        // count prev scatters. S doesn't exist on reel 0 and 4.
        for (let reel: number = 1; reel < reels.length - 1; reel++) {
            const stopSymbols: SimpleReelModelSymbolData[] = KennethMustDie.reelView.reelModels[reel].stopSymbols;
            for (let symId: number = 0; symId < stopSymbols.length; symId++) {
                if (stopSymbols[symId].name === SPECIAL_SYMBOL_NAMES.S) {
                    scatterCount++;
                    prevScatterReels.push(reel);

                    break;
                }
            }
        }

        if (scatterCount < 3) {
            // for reel id 1
            if (scatterCount === 2 && KennethMustDie.reelView.reelModels[1].stopSymbols.length < 3 && prevScatterReels.indexOf(1) === -1) {
                retVal[1] = true;
            }

            // S doesn't exist on reel 0 and 4.
            for (let reel: number = 1; reel < reels.length - 1; reel++) {
                if (reels[reel].includes(SPECIAL_SYMBOL_NAMES.S) && prevScatterReels.indexOf(reel) === -1) {
                    scatterCount++;
                }

                const nextReel: number = (reel + 1);
                if (scatterCount > 1 && (nextReel < (reels.length - 1)) && KennethMustDie.reelView.reelModels[nextReel].stopSymbols.length < 3 && prevScatterReels.indexOf(nextReel) === -1) {
                    retVal[nextReel] = true;
                }

                if (scatterCount === 3) {
                    break;
                }
            }
        }

        // check for S2 anticipation
        if (scatterCount === 3) {
            let prevS2: boolean = false;

            const stopSymbols: SimpleReelModelSymbolData[] = KennethMustDie.reelView.reelModels[4].stopSymbols;
            if (stopSymbols.length < 2) {
                for (let symId: number = 0; symId < stopSymbols.length; symId++) {
                    if (stopSymbols[symId].name === SPECIAL_SYMBOL_NAMES.S2) {
                        prevS2 = true;

                        break;
                    }
                }

                if (!prevS2) {
                    retVal[4] = true;
                }
            }
        }
    }

    private calculateReSpawnPositionsForStickyXNudge(reels: string[][]): void {
        // no sticky xNudge during normal or normal_avalanche mode, FS or FS_avalanche
        const mode: string = KennethMustDie.model.data.mode;
        if (mode.indexOf("NORMAL") > -1 || mode === KMDGameMode.FREESPIN || mode === KMDGameMode.FREESPIN_AVALANCHE) {
            return;
        }

        // only middle 3 main reels
        for (let reelId: number = 1; reelId < reels.length - 1; reelId++) {
            for (let symId: number = reels[reelId].length - 1; symId >= 0; symId--) {
                this.reSpawnPositions[reelId][symId] = (reels[reelId][symId] === SPECIAL_SYMBOL_NAMES.X_NUDGE_WILD || reels[reelId][symId] === SPECIAL_SYMBOL_NAMES.NW || this.reSpawnPositions[reelId][symId + 1]);

                // #326
                if (reels[reelId][symId] === SPECIAL_SYMBOL_NAMES.NW) {
                    this.multipliersForReelStop[reelId][symId] = 0;
                }
            }
        }
    }

    private calculateReSpawnPositionsForStickyWilds(reels: string[][]): void {
        // no sticky wilds in normal or normal_avalanche game mode
        if (this.data.mode.indexOf("NORMAL") > -1) {
            return;
        }

        // only middle 3 main reels
        for (let reelId: number = 1; reelId < reels.length - 1; reelId++) {
            for (let symId: number = 0; symId < reels[reelId].length; symId++) {
                if (reels[reelId][symId] === SPECIAL_SYMBOL_NAMES.W) {
                    this.reSpawnPositions[reelId][symId] = true;

                    // making the spin symbol to be a low symbol as W will be sticky there
                    if (!this.isRestore && KennethMustDie.stickyWilds.checkIfSticky(reelId, symId)) {
                        reels[reelId][symId] = ArrayHelper.getRandomValue(KennethMustDie.LOW_SYMBOL_NAMES);

                        // #326
                        this.multipliersForReelStop[reelId][symId] = this.data.multiplier[reelId][symId];
                    }
                }
            }
        }
    }
}